import { useState } from 'react'
import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Users } from 'lucide-react'
import { managementLayoutRoute } from '@/ui/pages/management'
import { useAuthStore } from '@/application/stores/auth.store'
import { classNames } from '@/ui/utils/main'
import {
  useGetAdminUsers,
  useEnableUser,
  useDisableUser,
  useUpdateUserRole,
  useDeleteAdminUser,
} from '@/application/services/user-admin.service'
import type { AdminUser } from '@/domains/types'
import { Button } from '@/ui/components/common/Button'
import { Badge } from '@/ui/components/common/Badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/ui/components/common/Dialog'

export const investigatorsRoute = createRoute({
  getParentRoute: () => managementLayoutRoute,
  path: 'investigators',
  component: InvestigatorsPage,
})

type FilterType = 'all' | 'pending'

const FILTER_TABS: FilterType[] = ['all', 'pending']

export function InvestigatorsPage() {
  const { t } = useTranslation()
  const { user: currentUser } = useAuthStore()
  const isAdmin = currentUser?.role === 'admin'
  const isPrivileged = isAdmin || currentUser?.role === 'streamer'

  const [filter, setFilter] = useState<FilterType>('all')
  const [page, setPage] = useState(1)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)

  const { data, isLoading } = useGetAdminUsers(filter, page)
  // Requête séparée pour le compteur « en attente » — indépendante du filtre actif
  const { data: pendingData } = useGetAdminUsers('pending', 1, 1)
  const pendingCount = pendingData?.total ?? 0

  const { mutateAsync: enableUser } = useEnableUser()
  const { mutateAsync: disableUser } = useDisableUser()
  const { mutateAsync: updateRole } = useUpdateUserRole()
  const { mutateAsync: deleteUser, isPending: isDeleting } = useDeleteAdminUser()

  const handleEnable = async (user: AdminUser) => {
    try {
      await enableUser(user.id)
      toast.success(t('admin.investigators_enabled_toast'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  const handleDisable = async (user: AdminUser) => {
    try {
      await disableUser(user.id)
      toast.success(t('admin.investigators_disabled_toast'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  const handleRoleChange = async (user: AdminUser, role: string) => {
    try {
      await updateRole({ id: user.id, role })
      toast.success(t('admin.investigators_role_updated'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteUser(deleteTarget.id)
      toast.success(t('admin.investigators_deleted'))
      setDeleteTarget(null)
    } catch {
      toast.error(t('admin.investigators_delete_failed'))
    }
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTER_TABS.map((filterTab) => (
          <button
            key={filterTab}
            onClick={() => {
              setFilter(filterTab)
              setPage(1)
            }}
            className={classNames(
              'px-3 py-1 rounded-full text-sm font-medium transition-colors border',
              filter === filterTab
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
            )}
          >
            {filterTab === 'pending'
              ? t('admin.investigators_tab_pending', { count: pendingCount })
              : t('admin.investigators_tab_all')}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_unused, index) => (
            <div key={index} className="h-14 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : !data?.users.length ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>
            {filter === 'pending'
              ? t('admin.investigators_pending_empty')
              : t('admin.investigators_empty')}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.investigators_col_user')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">
                  {t('admin.investigators_col_email')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.investigators_col_role')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.investigators_col_status')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">
                  {t('admin.investigators_col_date')}
                </th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                  {t('admin.investigators_col_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.users.map((adminUser) => (
                <tr key={adminUser.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
                        {(adminUser.displayName ?? adminUser.username ?? '?')
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">
                          {adminUser.displayName ?? adminUser.username ?? '—'}
                        </p>
                        {adminUser.displayName && adminUser.username && (
                          <p className="text-xs text-muted-foreground">@{adminUser.username}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                    {adminUser.email ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    {isPrivileged && adminUser.role !== 'streamer' ? (
                      <select
                        value={adminUser.role}
                        onChange={(event) => {
                          void handleRoleChange(adminUser, event.target.value)
                        }}
                        className="text-xs border border-border rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="user">{t('admin.investigators_role_user')}</option>
                        <option value="moderator">{t('admin.investigators_role_moderator')}</option>
                        <option value="admin">{t('admin.investigators_role_admin')}</option>
                      </select>
                    ) : (
                      <Badge variant="outline">
                        {adminUser.role === 'user'
                          ? t('admin.investigators_role_user')
                          : adminUser.role === 'moderator'
                            ? t('admin.investigators_role_moderator')
                            : adminUser.role}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isPrivileged && adminUser.role !== 'streamer' ? (
                      <select
                        value={adminUser.enabled ? 'enabled' : 'disabled'}
                        onChange={(event) => {
                          if (event.target.value === 'enabled') void handleEnable(adminUser)
                          else void handleDisable(adminUser)
                        }}
                        className="text-xs border border-border rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="enabled">{t('admin.investigators_enabled')}</option>
                        <option value="disabled">{t('admin.investigators_disabled')}</option>
                      </select>
                    ) : (
                      <Badge variant={adminUser.enabled ? 'default' : 'secondary'}>
                        {adminUser.enabled
                          ? t('admin.investigators_enabled')
                          : t('admin.investigators_disabled')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs hidden lg:table-cell">
                    {new Date(adminUser.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {isPrivileged && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setDeleteTarget(adminUser)}
                        >
                          {t('common.delete')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > data.limit && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {t('admin.pagination_info', {
              page: data.page,
              total: Math.ceil(data.total / data.limit),
            })}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              {t('common.page_prev')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * data.limit >= data.total}
              onClick={() => setPage((prev) => prev + 1)}
            >
              {t('common.page_next')}
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.investigators_delete_confirm')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            @{deleteTarget?.username ?? deleteTarget?.displayName}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t('common.cancel')}</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                void handleDelete()
              }}
              isLoading={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
